import { Box, Card, CardMedia, Chip, IconButton, MenuItem, Stack, Tooltip, Typography } from "@mui/material";
import { Customer, Tour } from "../pages/Projects";
import KebabMenu from "./KebabMenu";
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import DesktopWindowsIcon from '@mui/icons-material/DesktopWindows';
import DesktopWindowsOutlinedIcon from '@mui/icons-material/DesktopWindowsOutlined';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import PhoneIphoneOutlinedIcon from '@mui/icons-material/PhoneIphoneOutlined';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import { useState } from "react";
import { getTourPath } from "../utils";

interface Props {
  tour: Tour;
  customers: Customer[];
  deleteTour: (id: string) => void;
  onEditTourHandler: (tour: Tour) => void;
  swapElements: (index1: number, index2: number) => void;
  index: number;
  setFavoriteTour: (id: string) => void;
}

const TourCard = (props: Props) => {
  const { tour, customers, deleteTour, swapElements, onEditTourHandler, index, setFavoriteTour } = props;
  const { image, showOnDesktop, showOnMobile, subtitle, title, id, showOnFrontpage } = tour;

  const path = getTourPath(tour, customers);
  const [copied, setCopied] = useState(false);

  const copyLink = () => {
    if (!path) return;
    navigator.clipboard.writeText(`${window.location.origin}/${path}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <Card sx={{ overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ position: 'relative' }}>
        <CardMedia component="img" image={image} alt="" sx={{ height: 150, objectFit: 'cover', bgcolor: '#F4F5F9' }} />
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: 56,
            background: 'linear-gradient(to bottom, rgba(0,0,0,0.45), rgba(0,0,0,0))',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            px: 0.5,
          }}
        >
          <Tooltip title={showOnFrontpage ? "Vist på forsiden" : "Tilføj til forside"}>
            <IconButton
              size="small"
              onClick={() => setFavoriteTour(id ?? '')}
              sx={{ color: 'white', '&:hover': { bgcolor: 'rgba(255,255,255,0.15)' } }}
            >
              {showOnFrontpage ? <StarIcon fontSize="small" sx={{ color: '#FFC107' }} /> : <StarBorderIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
          <KebabMenu iconSx={{ color: 'white' }} buttonSx={{ '&:hover': { bgcolor: 'rgba(255,255,255,0.15)' } }}>
            <MenuItem onClick={() => onEditTourHandler(tour)}>Rediger</MenuItem>
            <MenuItem onClick={() => swapElements(index, index - 1)}>Ryk op</MenuItem>
            <MenuItem onClick={() => swapElements(index + 1, index)}>Ryk ned</MenuItem>
            <MenuItem onClick={() => deleteTour(id ?? '')}>Slet</MenuItem>
          </KebabMenu>
        </Box>
      </Box>

      <Stack spacing={1} sx={{ p: 2, flexGrow: 1 }}>
        <Box>
          <Typography variant="subtitle1" fontWeight={700} noWrap title={title}>{title}</Typography>
          <Typography variant="body2" color="text.secondary" noWrap title={subtitle}>{subtitle}</Typography>
        </Box>

        {path ? (
          <Tooltip title={copied ? 'Linket er kopieret!' : 'Klik for at kopiere linket'}>
            <Chip
              size="small"
              label={path}
              onClick={copyLink}
              icon={copied ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
              sx={{ alignSelf: 'flex-start', maxWidth: '100%', bgcolor: '#EEF2FF', color: '#4F46E5', '& .MuiChip-icon': { color: '#4F46E5' } }}
            />
          </Tooltip>
        ) : (
          <Chip
            size="small"
            icon={<WarningAmberIcon />}
            label="Mangler sti"
            color="warning"
            variant="outlined"
            sx={{ alignSelf: 'flex-start' }}
          />
        )}

        <Stack direction="row" spacing={2} sx={{ pt: 0.5 }}>
          <Tooltip title={showOnDesktop ? "Vises på desktop" : "Vises ikke på desktop"}>
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ color: showOnDesktop ? 'primary.main' : 'text.disabled' }}>
              {showOnDesktop ? <DesktopWindowsIcon fontSize="small" /> : <DesktopWindowsOutlinedIcon fontSize="small" />}
            </Stack>
          </Tooltip>
          <Tooltip title={showOnMobile ? "Vises på mobil" : "Vises ikke på mobil"}>
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ color: showOnMobile ? 'primary.main' : 'text.disabled' }}>
              {showOnMobile ? <PhoneIphoneIcon fontSize="small" /> : <PhoneIphoneOutlinedIcon fontSize="small" />}
            </Stack>
          </Tooltip>
        </Stack>
      </Stack>
    </Card>
  );
};

export default TourCard;
